import { TreeSelect } from "primereact/treeselect";
import { useEffect, useState } from "react";
import ConditionalRender from "../../helpers/ConditionalRender";
import InstanceApi from "../../api/instance/InstanceApi";
import HostInstanceTypes from "../../types/HostInstanceType";
import HostApi from "../../api/host/HostApi";
import DiskApi from "../../api/host/DiskApi";
import { stringFormatting } from "../../helpers/utils";
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostTarget from "../../types/host/HostTarget";

const AGGREGATION_LABELS: Record<string, string> = {
    avg: 'Average',
    sum: 'Sum',
    max: 'Latest',
    min: 'Minimum'
};

function Metrics(props: {
    setAggregation: Function,
    aggregation: string,
    entityType: string,
    metricType: string,
    datasourceId: string | number,
    hostId: string | number,
    disabled: boolean,
    setStatistic: Function,
    statistic: string,
    statisticError: string,
    isDelete: boolean,
    instances: InstanceTarget[],
    hosts: HostTarget[],
    setUnits?: Function,
    scopeTypes?: string[] | null,
    [propName: string]: any
}) {
    const {
        entityType,
        metricType,
        disabled,
        setStatistic,
        statistic,
        setAggregation,
        setUnits,
        instances,
        hosts,
        datasourceId,
        hostId,
        isDelete,
        statisticError,
        aggregation,
        scopeTypes
    } = props

    const [metricsList, setMetricsList] = useState<any[]>([]);

    const [supportedInstances, setSupportedInstances] = useState<HostInstanceTypes[]>([]);
    const [supportedHosts, setSupportedHosts] = useState<HostInstanceTypes[]>([]);


    useEffect(() => {
        const getInstanceTypes = async() => {
            const instancesSupported = await InstanceApi.getInstanceTypes()
            setSupportedInstances(instancesSupported)
        }

        const getHostsTypes = async() => {
            const hostsSupported = await HostApi.getHostTypes()
            setSupportedHosts(hostsSupported)
        }
        // Disk metrics are a fixed list served by name — no host/instance types needed.
        if (entityType === 'host') {
            void getHostsTypes()
        } else if (entityType === 'datasource') {
            void getInstanceTypes()
        }

    }, [entityType, metricType, datasourceId, hostId]);


    const getSupportedTypesList = (list: any[]) => {
        const scoped = scopeTypes && scopeTypes.length > 0 ? new Set(scopeTypes) : null;

        if (entityType === 'datasource') {
            if (scoped) {
                return supportedInstances?.filter(supportedType => scoped.has(supportedType.type));
            }
            if (datasourceId) {
                // @ts-ignore
                const selectedInstance: InstanceTarget = instances.find(inst => inst.id === datasourceId)
                return supportedInstances?.filter(supportedType => supportedType.type === selectedInstance?.type);
            }
            return supportedInstances?.filter(supportedType =>
                list.some(stat => stat.type === supportedType.type)
            );
        }

        if (entityType === 'host') {
            if (scoped) {
                return supportedHosts.filter(supportedType => scoped.has(supportedType.type));
            }
            if (hostId) {
                // @ts-ignore
                const selectedHost: HostTarget = hosts.find(inst => inst.id === hostId)
                return supportedHosts.filter(supportedType => supportedType.type === selectedHost.type);
            }
            return supportedHosts.filter(supportedType =>
                list.some(stat => stat.type === supportedType.type)
            );
        }
        return []
    }

    useEffect(() => {
        const ready =
            (entityType === 'disk' || entityType === 'availabilitygroup' || entityType === 'job')
                ? true
                : entityType === 'host'
                ? supportedHosts.length > 0
                : entityType === 'datasource' && supportedInstances.length > 0;
        if (!ready) {
            setMetricsList([]);
            return;
        }

        let cancelled = false;

        const loadMetrics = async () => {
            let list: any[] = [];

            // Disk metrics are a flat list (no per-host-type grouping). Names,
            // units and aggregation all come from the API; dedupe by statistic
            // since the type endpoint returns one row per host type.
            if (entityType === 'job') {
                list = [
                    ['successrate', 'Success rate', 'avg', '%'],
                    ['failed', 'Failed executions', 'sum', ''],
                    ['succeeded', 'Succeeded executions', 'sum', ''],
                    ['retry', 'Retried executions', 'sum', ''],
                    ['cancelled', 'Cancelled executions', 'sum', ''],
                    ['inprogress', 'Executions in progress', 'sum', ''],
                    ['executions', 'Executions', 'sum', ''],
                ].map(([key, label, aggregation, units]) => ({ key, label, aggregation, units }));
            } else if (entityType === 'availabilitygroup') {
                list = [
                    ['agdatabasesnothealthy', 'Databases not healthy'],
                    ['agreplicasnothealthy', 'Replicas disconnected or resolving'],
                    ['aglistenersoffline', 'Listeners offline'],
                    ['agnotfailoverready', 'Databases not ready for failover'],
                    ['agfailovers', 'Cluster failovers'],
                ].map(([key, label]) => ({ key, label, aggregation: 'max', units: '' }));
            } else if (entityType === 'disk') {
                const diskTypes = await DiskApi.getDiskStatisticTypes();
                if (cancelled) {
                    return;
                }
                const seen = new Set<string>();
                const children = diskTypes.reduce((acc: any[], item: any) => {
                    if (!item.statistic || seen.has(item.statistic)) return acc;
                    seen.add(item.statistic);
                    acc.push({
                        key: item.statistic,
                        label: stringFormatting(item.statistic),
                        data: item.statistic,
                        statistic: item.statistic,
                        type: 'disk',
                        units: item.units,
                        aggregation: item.aggregation
                    });
                    return acc;
                }, []);
                if (children.length) {
                    list = [{ key: 'disk', label: 'Disk', selectable: false, children }];
                }
                setMetricsList(list);
                return;
            }

            let metricTypes: any[] = [];
            if (entityType === 'datasource') {
                if (metricType === 'status') {
                    metricTypes = await InstanceApi.getInstanceStatusTypes()
                } else {
                    metricTypes = await InstanceApi.getInstanceStatisticTypes(metricType === 'activity')
                }
            } else {
			    if (metricType === 'status') {
                    metricTypes = await HostApi.getHostStatusTypes()
                } else {
                    metricTypes = await HostApi.getHostStatisticTypes()
                }
            }
            if (cancelled) {
                return;
            }
            const supportedMetrics = getSupportedTypesList(metricTypes)
            if (supportedMetrics.length && metricTypes.length) {
                list = supportedMetrics.map((metric: any) => {
                    return {
                        "key": metric.type,
                        "label": metric.display,
                        "selectable": false,
                        children: metricTypes.filter((item: any) => item.type === metric.type).map((item: any) => ({
                            key: item.statistic,
                            label: stringFormatting(item.statistic),
                            data: item.statistic,
                            statistic: item.statistic,
                            type: item.type,
                            units: item.units,
                            cumulative: item.cumulative,
                            aggregation: item.aggregation,
                            collect: item.collect,
                            description: item.description
                        })).sort((a: any, b: any) => a.label.localeCompare(b.label))
                    };
                })
            }
            setMetricsList(list)
        };

        void loadMetrics();
        return () => {
            cancelled = true;
        };
    }, [
        entityType,
        metricType,
        datasourceId,
        hostId,
        scopeTypes,
        supportedInstances,
        supportedHosts,
        instances,
        hosts
    ]);

    const findNode = (nodes: any[], key: string): { node: any, group: any } | null => {
        const matches = nodes.flatMap(group =>
            (group.children ?? [])
                .filter((child: any) => child.key === key)
                .map((node: any) => ({ node, group }))
        )
        if (!matches.length) return null
        return { node: matches[0].node, group: matches.length === 1 ? matches[0].group : null }
    }

    const selected = statistic ? findNode(metricsList, statistic) : null
    const aggregationLabel = AGGREGATION_LABELS[aggregation] ?? (aggregation ? stringFormatting(aggregation) : '')
    const showMeta = !!selected && !isDelete

    const valueTemplate = (selectedNodes: any) => {
        const node = Array.isArray(selectedNodes) ? selectedNodes[0] : selectedNodes
        if (!node) return <span className="p-placeholder">Select metric</span>
        return (
            <span className="metric-picker-value">
                <ConditionalRender if={!!selected?.group}>
                    <span className="metric-picker-group">{selected?.group?.label}</span>
                    <i className="fal fa-angle-right fa-fw metric-picker-sep" aria-hidden="true"/>
                </ConditionalRender>
                <span className="metric-picker-label">{node.label}</span>
            </span>
        )
    }

    return (
        <>
            <div className="col-xs-12 col-md-3">
                <span className="required">
                    <label htmlFor="metric" className="reminder">Metric</label>
                </span>
            </div>
            <div className="col-xs-12 col-md-9 col-lg-6">
                <TreeSelect inputId="metric"
                            value={statistic}
                            options={metricsList}
                            className={`metric-picker w-100 ${statisticError === 'invalid' && !statistic ? 'is-invalid' : ''}`}
                            panelClassName="metric-picker-panel"
                            onChange={(e) => setStatistic(e.value)}
                            onNodeSelect={(e: any) => {
                                setAggregation(e.node.aggregation)
                                if (setUnits) setUnits(e.node.units ?? '')
                            }}
                            valueTemplate={valueTemplate}
                            filter
                            filterPlaceholder="Search metrics"
                            emptyMessage="No metrics available"
                            disabled={disabled}
                            placeholder="Select metric"/>
                <ConditionalRender if={showMeta}>
                    <small className="text-muted metric-picker-meta">
                        <span><i className="fal fa-calculator fa-fw" aria-hidden="true"/> {aggregationLabel}</span>
                        <ConditionalRender if={!!selected?.node?.units}>
                            <span><i className="fal fa-ruler-horizontal fa-fw" aria-hidden="true"/> {selected?.node?.units}</span>
                        </ConditionalRender>
                        <ConditionalRender if={!!selected?.node?.description}>
                            <span className="metric-picker-description">{selected?.node?.description}</span>
                        </ConditionalRender>
                    </small>
                </ConditionalRender>
                <ConditionalRender if={statisticError && !statistic}>
                    <small className="text-danger">Metric is Required</small>
                </ConditionalRender>
            </div>
            <div className="w-100"/>
        </>
    )
}

export default Metrics;
